import React from 'react'
import ReactTooltip from 'react-tooltip'
import { WrenchIcon } from '@phosphor-icons/react'

import DropdownDots from '../../../../../components/DropdownDots/DropdownDots'
import Icon from '../../../../../components/Icons'
import EditableDaysCell from '../../components/EditableDaysCell/EditableDaysCell'
import { getAssetLink } from '../../../../../helpers/get-asset-link'

export const checkListOptions = ({ task, onEdit = () => {}, t }) => [
  {
    id: 1,
    text: t('checklists-settings:shared.editOption'),
    action: () => onEdit(task),
  },
]

export const getItemStyle = (isDragging, draggableStyle) => {
  return {
    marginBottom: 15,
    backgroundColor: isDragging ? '#FDF9F4' : '#ffffff',
    ...draggableStyle,
  }
}

export const getCustomTasksFilterOptions = (t) => [
  {
    id: 0,
    text: t('checklists-settings:customTasks.filterOptions.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('checklists-settings:customTasks.filterOptions.templates'),
    value: 'template',
  },
  {
    id: 2,
    text: t('checklists-settings:customTasks.filterOptions.custom'),
    value: 'custom',
  },
]

const getTypeSettings = (type, t) => {
  switch (type) {
    case 'template':
      return {
        icon: 'galleryViewEdit',
        className: 'type-badge__template',
        tooltipText: t('checklists-settings:customTasks.templateTooltip'),
      }
    case 'integration':
      return {
        icon: 'stars2',
        className: 'type-badge__integration',
        tooltipText: t('checklists-settings:customTasks.integrationTooltip'),
      }
    default:
      return {}
  }
}

export const customTasksColumns = ({
  onEdit = () => {},
  onDuplicate = () => {},
  onDelete = () => {},
  userRole,
  t,
}) => {
  return [
    {
      Header: () => (
        <React.Fragment>
          {t('checklists-settings:customTasks.taskNameHeader')}
          <ReactTooltip className="type-badge__tooltip" id="type-tip" effect="solid" place="top" />
        </React.Fragment>
      ),
      id: 'header_info',

      Cell: ({ row: { original } }) => {
        const { name, integrationProvider, source, isTemplate, serviceTask } = original
        const type = integrationProvider
          ? 'integration'
          : source === 'system' && isTemplate
            ? 'template'
            : ''

        const { className, icon, tooltipText } = getTypeSettings(type, t)
        return (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {name}
            {!!icon && (
              <div
                data-tip={tooltipText}
                data-for="type-tip"
                style={{ position: 'relative' }}
                className={`type-badge ${className}`}
              >
                <Icon
                  icon={icon}
                  svgProps={{
                    fillRule: 'evenodd',
                    clipRule: 'evenodd',
                  }}
                />
              </div>
            )}
            {serviceTask && (
              <div
                data-tip={t('checklists-settings:customTasks.integrationServiceTaskTooltip')}
                data-for="type-tip"
                className="type-badge type-badge__service"
              >
                <WrenchIcon size={16} />
              </div>
            )}
          </div>
        )
      },
    },
    {
      Header: t('checklists-settings:customTasks.sourceHeader'),

      Cell: ({ row: { original } }) => {
        const { source, buildingId } = original
        const isSystem = source === 'system'
        return (
          <div className="ctp-checklists-custom-tasks__source">
            <img
              src={
                isSystem
                  ? '/images/logo/elevateos-logo-square-black.png'
                  : getAssetLink({ path: 'chitown-building', file: buildingId.image })
              } // TODO: replace with actual building logo
              alt={t('checklists-settings:customTasks.buildingLogoAlt')}
            />
            {isSystem ? t('checklists-settings:customTasks.systemSourceName') : buildingId.name}
          </div>
        )
      },
    },
    {
      Header: t('checklists-settings:customTasks.assignTriggerHeader'),
      Cell: (data) => {
        const { id, assignTaskRelative, assignTaskDays, assignTaskTrigger } = data.row.original
        return (
          <EditableDaysCell
            data={{
              id,
              condition: assignTaskRelative,
              trigger: assignTaskTrigger,
              days: assignTaskDays,
              name: 'assignTask',
            }}
          />
        )
      },
    },
    {
      Header: t('checklists-settings:customTasks.dueDateHeader'),
      Cell: (data) => {
        const { id, dueDateCondition, dueDateDays, dueDateTrigger } = data.row.original
        return (
          <EditableDaysCell
            data={{
              id,
              condition: dueDateCondition,
              trigger: dueDateTrigger,
              days: dueDateDays,
              name: 'dueDate',
            }}
          />
        )
      },
    },
    {
      Header: t('checklists-settings:shared.actionsHeader'),
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const isIntegration = !!original.integrationProvider
        const editOption = {
          id: 1,
          text: t('checklists-settings:shared.editOption'),
          action: () => onEdit(original),
        }

        if (isIntegration) {
          return (
            <div className="actions-cell">
              <DropdownDots options={[editOption]} />
            </div>
          )
        }

        const options = [
          editOption,
          {
            id: 2,
            text: t('checklists-settings:customTasks.duplicateOption'),
            action: () => onDuplicate(original),
          },
        ]
        if (!original.isTemplate || (original.isTemplate && userRole === 'Administrator')) {
          options.push({
            id: 3,
            text: t('checklists-settings:shared.deleteOption'),
            action: () => onDelete(original),
          })
        }

        return (
          <div className="actions-cell">
            <DropdownDots options={options} />
          </div>
        )
      },
    },
  ]
}

export const getIntegrationLabel = (selectedBuilding) => {
  if (!selectedBuilding) return ''

  const isIntegratedRealpage =
    selectedBuilding.realPageManagementId && selectedBuilding.realPagePropertyId
  const inIntegratedEntrata =
    selectedBuilding.entrataManagementId && selectedBuilding.entrataPropertyId
  const isIntegratedYardi = selectedBuilding.yardiManagementId && selectedBuilding.yardiPropertyId
  const isIntegratedRentManager =
    selectedBuilding.rentManagerManagementId && selectedBuilding.rentManagerPropertyId

  if (isIntegratedYardi) {
    return 'Yardi'
  }

  if (inIntegratedEntrata) {
    return 'Entrata'
  }

  if (isIntegratedRealpage) {
    return 'Real Page'
  }

  if (isIntegratedRentManager) {
    return 'Rent Manager'
  }

  return ''
}

const allowedIntegrationTypes = ['Yardi', 'Entrata', 'Real Page', 'Rent Manager']
export const getIntegrationIcon = (label) => {
  if (!allowedIntegrationTypes.includes(label)) {
    return null
  }

  return {
    Yardi: '/images/Yardiicon.jpg',
    Entrata: '/images/Entrata.jpg',
    'Real Page': '/images/realpage-inspection.png',
    'Rent Manager': '/images/RentManager.jpg',
  }[label]
}
